import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { productSlugFromName } from './productSlug';

export type OkeCompartmentValue = {
  mode?: 'existing' | 'create-tree';
  parentCompartmentOcid?: string;
  parentCompartmentLabel?: string;
  treeRootName?: string;
  treeFirstChildName?: string;
};

type DiscoveryOption = {
  value: string;
  label: string;
  description?: string;
};

export const TechneOkeCompartmentFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      mode: z.enum(['existing', 'create-tree']).optional(),
      parentCompartmentOcid: z.string().optional(),
      parentCompartmentLabel: z.string().optional(),
      treeRootName: z.string().optional(),
      treeFirstChildName: z.string().optional(),
    }),
  uiOptions: z => z.object({}),
});

function isTreeParentOcid(value: string): boolean {
  const id = value.trim();
  return id.startsWith('ocid1.compartment.') || id.startsWith('ocid1.tenancy.');
}

function parseValue(raw: unknown): OkeCompartmentValue {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as OkeCompartmentValue;
  }
  return { mode: 'existing' };
}

export function TechneOkeCompartmentField(
  props: FieldExtensionComponentProps<OkeCompartmentValue>,
) {
  const { onChange, rawErrors, required, formData, formContext } = props;
  const value = parseValue(formData);
  const mode = value.mode === 'create-tree' ? 'create-tree' : 'existing';
  const treeActive = mode === 'create-tree';

  const rootFormData = (formContext?.formData ?? {}) as Record<string, unknown>;
  const productSlug =
    productSlugFromName(String(rootFormData.productName ?? '').trim()) ||
    'slug';
  const environment = String(rootFormData.environment ?? '').trim() || 'env';
  const ociRegion = String(rootFormData.ociRegion ?? '').trim();

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [items, setItems] = useState<DiscoveryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [paiDraft, setPaiDraft] = useState(value.treeRootName ?? '');
  const [filhoDraft, setFilhoDraft] = useState(value.treeFirstChildName ?? '');
  const [cardError, setCardError] = useState<string | null>(null);

  useEffect(() => {
    if (!cardOpen) {
      setPaiDraft(value.treeRootName ?? '');
      setFilhoDraft(value.treeFirstChildName ?? '');
    }
  }, [cardOpen, value.treeRootName, value.treeFirstChildName]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setListError(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('oracle-providers');
        const { token } = await identityApi.getCredentials();
        const params = new URLSearchParams();
        if (ociRegion) {
          params.set('region', ociRegion);
        }
        const qs = params.toString();
        const url = `${baseUrl}/discovery/compartments${qs ? `?${qs}` : ''}`;
        const response = await fetchApi.fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = (await response.json()) as {
          options?: DiscoveryOption[];
        };
        if (cancelled) return;
        setItems(Array.isArray(payload.options) ? payload.options : []);
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        setListError(
          err instanceof Error ? err.message : 'Erro ao listar compartments',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, identityApi, ociRegion]);

  const selected = useMemo(
    () =>
      items.find(item => item.value === value.parentCompartmentOcid) ?? null,
    [items, value.parentCompartmentOcid],
  );

  const applyTree = () => {
    const pai = paiDraft.trim();
    const filho = filhoDraft.trim();
    const parentOcid = String(value.parentCompartmentOcid ?? '').trim();
    if (!isTreeParentOcid(parentOcid)) {
      setCardError('Escolha o compartment pai no select primeiro.');
      return;
    }
    if (!pai || !filho) {
      setCardError('Preencha os dois nomes da estrutura nova.');
      return;
    }
    setCardError(null);
    onChange({
      mode: 'create-tree',
      parentCompartmentOcid: parentOcid,
      parentCompartmentLabel: value.parentCompartmentLabel ?? selected?.label,
      treeRootName: pai,
      treeFirstChildName: filho,
    });
    setCardOpen(false);
  };

  const revertExisting = () => {
    onChange({
      mode: 'existing',
      parentCompartmentOcid: value.parentCompartmentOcid ?? '',
      parentCompartmentLabel: value.parentCompartmentLabel ?? '',
      treeRootName: '',
      treeFirstChildName: '',
    });
    setCardOpen(false);
    setCardError(null);
  };

  const fieldError = (rawErrors?.length ?? 0) > 0 || Boolean(listError);

  return (
    <Box marginTop={1} marginBottom={2}>
      <Autocomplete
        options={items}
        value={selected}
        disabled={loading}
        getOptionLabel={item => item.label}
        getOptionSelected={(a, b) => a.value === b.value}
        filterOptions={(opts, state) => {
          const query = state.inputValue.trim().toLowerCase();
          if (!query) return opts;
          return opts.filter(
            item =>
              item.label.toLowerCase().includes(query) ||
              (item.description ?? '').toLowerCase().includes(query) ||
              item.value.toLowerCase().includes(query),
          );
        }}
        onChange={(_event, item) =>
          onChange({
            mode: treeActive ? 'create-tree' : 'existing',
            parentCompartmentOcid: item?.value ?? '',
            parentCompartmentLabel: item?.label ?? '',
            treeRootName: treeActive ? value.treeRootName : '',
            treeFirstChildName: treeActive ? value.treeFirstChildName : '',
          })
        }
        renderOption={item => (
          <Box>
            <Typography variant="body2">{item.label}</Typography>
            {item.description && item.description !== item.label ? (
              <Typography variant="caption" color="textSecondary">
                {item.description}
              </Typography>
            ) : null}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Compartment pai"
            required={required}
            error={fieldError}
            placeholder={
              loading
                ? 'Carregando…'
                : 'Digite para filtrar (ex. Techne)'
            }
            helperText={
              treeActive
                ? `Pai: ${value.parentCompartmentLabel || selected?.label || 'escolha no select'} → ${value.treeRootName} → ${value.treeFirstChildName}. OKE ${productSlug}-${environment} no backstage, sem compartment extra.`
                : listError ||
                  'Produto existente, ou pai de uma estrutura nova. (root) cria o primeiro compartment na tenancy.'
            }
            InputLabelProps={{ shrink: true }}
          />
        )}
      />

      <Box display="flex" alignItems="center" marginTop={1} style={{ gap: 8 }}>
        <Button
          variant={treeActive || cardOpen ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          startIcon={<AccountTreeIcon />}
          onClick={() => {
            if (!isTreeParentOcid(String(value.parentCompartmentOcid ?? ''))) {
              setCardOpen(true);
              setCardError('Escolha o compartment pai no select primeiro.');
              return;
            }
            if (cardOpen) {
              const pai = paiDraft.trim();
              const filho = filhoDraft.trim();
              if (pai && filho) {
                applyTree();
                return;
              }
              setCardOpen(false);
              return;
            }
            setCardOpen(true);
            setCardError(null);
          }}
        >
          Nova estrutura de compartment
        </Button>
        {treeActive ? (
          <Tooltip title="Voltar a escolher um produto existente">
            <IconButton
              size="small"
              onClick={revertExisting}
              aria-label="Desmarcar estrutura nova"
            >
              <CheckCircleIcon style={{ color: '#2e7d32' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {cardOpen ? (
        <Card variant="outlined" style={{ marginTop: 12 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Criar debaixo do pai escolhido
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              O select de cima é o pai (já existe), inclusive (root). Abaixo
              dele: nome 1 → nome 2, com network e backstage irmãos. O cluster
              OKE {productSlug}-{environment} fica no backstage, sem
              compartment de produto.
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              style={{
                fontFamily: 'monospace',
                background: 'rgba(255,255,255,0.04)',
                padding: 12,
                borderRadius: 4,
                overflowX: 'auto',
              }}
            >
              {`${value.parentCompartmentLabel || selected?.label || '(escolha o pai)'}
 └── ${paiDraft.trim() || 'Tecnologia'}
      └── ${filhoDraft.trim() || 'tecnologia-dev'}
           ├── network
           └── backstage
                 (cluster OKE)`}
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              label="1º filho (ex. Tecnologia)"
              placeholder="Ex. Tecnologia"
              value={paiDraft}
              onChange={event => setPaiDraft(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="2º nível (ex. tecnologia-dev)"
              placeholder="Ex. tecnologia-dev"
              value={filhoDraft}
              onChange={event => setFilhoDraft(event.target.value)}
              helperText="Network e backstage nascem neste nível."
              InputLabelProps={{ shrink: true }}
            />
            {cardError ? (
              <FormHelperText error>{cardError}</FormHelperText>
            ) : null}
          </CardContent>
          <CardActions>
            <Button size="small" onClick={() => setCardOpen(false)}>
              Cancelar
            </Button>
            <Button size="small" color="primary" onClick={applyTree}>
              Usar esta estrutura
            </Button>
          </CardActions>
        </Card>
      ) : null}
    </Box>
  );
}
